import { SlicePipe } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core'
import { ActivatedRoute, RouterLink } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { forkJoin } from 'rxjs'
import { finalize, map } from 'rxjs/operators'

import { ArticlesService } from '../../../../../api/articles-service/articles.service'
import type {
  ArticlesDto,
  EditionDto,
  MagazineDto,
} from '../../../../../api/responses'
import { ArticleList } from '../../../../../components/article-list/article-list'
import type {
  ArticleWithNullableRefs,
  EditionGroup,
} from '../../../../../types/types'

/** Stable empty reference so template bindings don't change identity every run. */
const NO_ARTICLES: ArticleWithNullableRefs[] = []

/** Parses an edition version such as `"5"` or `"5/6"` into a sortable number. */
function parseVersion(version: string | null | undefined): number {
  if (!version) return 0

  // For versions like "5/6", extract the first number
  const match = /^(\d+)/.exec(version)
  if (match?.[1]) {
    return parseInt(match[1], 10)
  }
  return parseInt(version, 10) || 0
}

@Component({
  selector: 'dhp-magazine-edition-overview',
  templateUrl: './magazine-edition-overview.component.html',
  styleUrls: ['./magazine-edition-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, ArticleList, SlicePipe, TranslatePipe],
})
export class MagazineEditionOverviewComponent {
  private articleService = inject(ArticlesService)
  route = inject(ActivatedRoute)

  readonly magazine = input<MagazineDto>()
  readonly editions = input<EditionDto[]>()
  readonly selectedYear = input<number | null>(null)

  readonly articles = signal<Record<string, ArticlesDto[]>>({})
  readonly loading = signal(false)
  readonly error = signal<string | null>(null)

  /** Editions narrowed down by the currently selected year. */
  private readonly visibleEditions = computed<EditionDto[]>(() => {
    const editions = this.editions() ?? []
    const selectedYear = this.selectedYear()

    return selectedYear !== null
      ? editions.filter(edition => edition.year === selectedYear)
      : editions
  })

  /** Visible editions grouped per year, newest year (and edition) first. */
  readonly groupedEditions = computed<EditionGroup[]>(() => {
    const groups = this.visibleEditions().reduce<Record<number, EditionDto[]>>(
      (acc, edition) => {
        const year = edition.year
        if (year !== undefined) {
          acc[year] ??= []
          acc[year].push(edition)
        }
        return acc
      },
      {}
    )

    return Object.entries(groups)
      .map(([year, editions]) => ({
        year: parseInt(year),
        editions: [...editions].sort((a, b) => {
          // Primary sort by volume if available
          const volumeA = a.volume ?? 0
          const volumeB = b.volume ?? 0
          if (volumeA !== volumeB) {
            return volumeB - volumeA
          }

          // Secondary sort by edition version for consistent display
          const versionA = parseVersion(a.edition_version)
          const versionB = parseVersion(b.edition_version)
          if (versionA !== versionB) {
            return versionB - versionA
          }

          // Fallback to published_at date if all else is equal
          return (
            new Date(b.published_at ?? '').getTime() -
            new Date(a.published_at ?? '').getTime()
          )
        }),
      }))
      .sort((a, b) => b.year - a.year)
  })

  /** Articles mapped to the shape `dhp-article-list` expects, per edition id. */
  private readonly mappedArticles = computed<
    Record<string, ArticleWithNullableRefs[]>
  >(() =>
    Object.fromEntries(
      Object.entries(this.articles()).map(([editionId, editionArticles]) => [
        editionId,
        editionArticles.map(article => this.mapArticle(article)),
      ])
    )
  )

  constructor() {
    // Refetch whenever the (filtered) editions change. Signal state keeps the
    // template in sync, so no manual change detection is required.
    effect(onCleanup => {
      const editions = this.visibleEditions()

      this.articles.set({})
      this.error.set(null)

      if (editions.length === 0) {
        this.loading.set(false)
        return
      }

      this.loading.set(true)

      const subscription = forkJoin(
        editions.map(edition =>
          this.articleService
            .getArticlesByEditionId(edition.id, {
              limit: 6,
              fetchDetails: true,
            })
            .pipe(map(articles => ({ editionId: edition.id, articles })))
        )
      )
        .pipe(
          finalize(() => {
            this.loading.set(false)
          })
        )
        .subscribe({
          next: results => {
            this.articles.set(
              Object.fromEntries(
                results.map(result => [
                  result.editionId.toString(),
                  Array.isArray(result.articles) ? result.articles : [],
                ])
              )
            )
          },
          error: () => {
            this.error.set('Failed to load articles')
            this.articles.set({})
          },
        })

      onCleanup(() => {
        subscription.unsubscribe()
      })
    })
  }

  truncateTitle(title: string): string {
    return title.length > 65 ? title.slice(0, 65) + '...' : title
  }

  getArticlesForEdition(editionId: number): ArticleWithNullableRefs[] {
    return this.mappedArticles()[editionId.toString()] ?? NO_ARTICLES
  }

  private mapArticle(article: ArticlesDto): ArticleWithNullableRefs {
    return {
      ...article,
      magazine: article.magazine
        ? ({
            ...article.magazine,
            title: article.magazine.title ?? undefined,
          } as MagazineDto & { title: string | null })
        : null,
      edition: article.edition
        ? {
            ...article.edition,
            edition_version: article.edition.edition_version ?? null,
          }
        : null,
      users: {
        authors: article.users?.authors?.map(author => ({
          ...author,
          linkType: author.type ?? 'author',
          id: author.id || 0, // Ensure id is always a number
        })),
      },
    }
  }
}
